import i18n from '../i18n/config';

import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';
/**
 * DealManager class handles the dealing of cards during a Ronda game.
 * 
 * Features:
 * - Manages the initial deal (4 cards face-up to center, 3 cards to each player)
 * - Handles subsequent deals (3 cards to each player only, no center cards)
 * - Detects duplicate values in center cards and reinserts them into the deck
 * - Deals cards in counter-clockwise order starting from dealer's right
 * - Shows toast notifications for game events
 * 
 * Dealing order:
 * The deal starts with the next player after the dealer and continues
 * around until the dealer receives the last card. Examples:
 * - If dealer is Player 1 (bottom): Player 2, Player 3, Player 4, Player 1
 * - If dealer is Player 2 (right): Player 3, Player 4, Player 1, Player 2
 * - If dealer is Player 3 (top): Player 4, Player 1, Player 2, Player 3
 * - If dealer is Player 4 (left): Player 1, Player 2, Player 3, Player 4
 */
class DealManager {
    constructor(deck, toastManager) {
        this.deck = deck;
        this.toastManager = toastManager;
        this.isFirstDeal = true;
        this.centerCards = [];
        this.discardedCards = []; // Cards discarded during "paso" in Baciga
        this.collectedCards = {
            team1: [], // Cards collected by team 1 (players 0 and 2)
            team2: []  // Cards collected by team 2 (players 1 and 3)
        };
        this.playerCards = {
            0: [], // Player 1 (bottom) - human player, cards face-up
            1: [], // Player 2 (right)
            2: [], // Player 3 (top)
            3: []  // Player 4 (left)
        };
    }

    /**
     * Get the dealing order based on the dealer's position
     * @param {number} dealerIndex - The index of the dealer (0-3)
     * @returns {number[]} Array of player indices in dealing order
     */
    getDealingOrder(dealerIndex) {
        // Start with the next player after the dealer and iterate
        // forward so the dealer receives the last card.
        const order = [];
        for (let i = 1; i <= 4; i++) {
            order.push((dealerIndex + i) % 4);
        }
        return order;
    }

    /**
     * Update data for a specific card in a player's hand
     * @param {number} playerIndex
     * @param {number} cardIndex
     * @param {Object} updates - Object with properties to update (e.g. { assignedValue: 7 })
     * @returns {boolean} True if successful
     */
    updateCardData(playerIndex, cardIndex, updates) {
        if (this.playerCards[playerIndex] && this.playerCards[playerIndex][cardIndex]) {
            const card = this.playerCards[playerIndex][cardIndex];

            // IMPORTANT: Prevent re-binding of wildcards (comodines)
            // Once a wildcard has an assignedValue, it should NOT be changed during the same hand
            // This prevents the bug where a wildcard bound to 1 for baciga declaration
            // gets re-bound to a different value during Las 31 scoring
            if (updates.assignedValue !== undefined &&
                card.assignedValue !== undefined &&
                card.assignedValue !== null) {
                // Wildcard is already bound - block the re-binding attempt
                debugWarn(`⚠️ [DealManager] BLOCKED re-binding attempt for player ${playerIndex} card ${cardIndex}: ` +
                    `${card.value} ${card.suit} already bound to ${card.assignedValue}, attempted to change to ${updates.assignedValue}`);
                return false;
            }

            Object.assign(card, updates);
            debugLog(`🃏 [DealManager] Updated card for player ${playerIndex} index ${cardIndex}:`, updates);
            return true;
        }
        return false;
    }

    /**
     * Deal cards for a new hand
     * @param {number} dealerIndex - The index of the dealer (0-3)
     * @param {boolean} isFirstHand - Whether this is the first hand of the round
     * @returns {Object} Object containing centerCards and playerCards
     */
    async dealHand(dealerIndex, isFirstHand = null) {
        // If isFirstHand is not explicitly set, use the internal flag
        const dealToCenter = isFirstHand !== null ? isFirstHand : this.isFirstDeal;

        // Reset player cards for new deal
        this.playerCards = {
            0: [],
            1: [],
            2: [],
            3: []
        };

        if (dealToCenter) {
            // First hand: deal to center and players alternately
            await this.dealFirstHand(dealerIndex);
            this.isFirstDeal = false;
        } else {
            // Subsequent hands: deal only to players
            this.dealSubsequentHand(dealerIndex);
        }

        return {
            centerCards: this.centerCards,
            playerCards: this.playerCards
        };
    }

    /**
     * Deal the first hand with center cards
     * Process: center card -> player cards -> center card -> player cards (3 times)
     * Result: 4 center cards, 3 cards per player
     */
    async dealFirstHand(dealerIndex) {
        this.centerCards = [];
        const dealingOrder = this.getDealingOrder(dealerIndex);

        // Repeat 3 times: deal 1 center card, then 1 card to each player
        for (let round = 0; round < 3; round++) {
            // Deal one card to center
            await this.dealCenterCard();

            // Deal one card to each player in counter-clockwise order
            for (const playerIndex of dealingOrder) {
                const card = this.deck.dealOne();
                if (card) {
                    this.playerCards[playerIndex].push(card);
                }
            }
        }

        // Deal the fourth and final center card
        await this.dealCenterCard();
    }

    /**
     * Deal a card to the center, checking for duplicates
     * If duplicate is found, reinsert into deck and deal again
     */
    async dealCenterCard() {
        let card = this.deck.dealOne();
        if (!card) return;

        // Check for duplicates in center cards
        let attempts = 0;
        const maxAttempts = 50; // Prevent infinite loop

        while (this.hasDuplicateValue(card) && attempts < maxAttempts) {
            // Show toast notification about duplicate
            const cardValueName = this.getCardValueName(card.value);
            const message = i18n.t('game.duplicate_card_center', { value: cardValueName })
                || `Carta ${cardValueName} repetida en el centro - reinsertando en el mazo`;

            if (this.toastManager) {
                this.toastManager.showToast(message, {
                    type: 'warning',
                    duration: 2500,
                    position: 'center'
                });
            }

            // Reinsert card into deck at random position
            this.deck.insertAtRandomPosition(card);

            // Deal a new card
            card = this.deck.dealOne();
            if (!card) return; // No more cards available

            attempts++;
        }

        if (attempts >= maxAttempts) {
            console.error('Max attempts reached while dealing center card - possible deck issue');
        }

        this.centerCards.push(card);
    }

    /**
     * Check if a card's value already exists in center cards
     */
    hasDuplicateValue(card) {
        return this.centerCards.some(centerCard => centerCard.value === card.value);
    }

    /**
     * Get the name of a card value for display
     */
    getCardValueName(value) {
        const valueNames = {
            1: 'As',
            2: '2',
            3: '3',
            4: '4',
            5: '5',
            6: '6',
            7: '7',
            10: 'Sota',
            11: 'Caballo',
            12: 'Rey'
        };
        return valueNames[value] || value.toString();
    }

    /**
     * Deal subsequent hands (only to players, no center cards)
     */
    dealSubsequentHand(dealerIndex) {
        const dealingOrder = this.getDealingOrder(dealerIndex);

        // Deal 3 cards to each player
        for (let round = 0; round < 3; round++) {
            for (const playerIndex of dealingOrder) {
                const card = this.deck.dealOne();
                if (card) {
                    this.playerCards[playerIndex].push(card);
                }
            }
        }
    }

    /**
     * Deal cards for Baciga game - 3 cards to each player, no center cards
     * According to rules: "Se reparten 3 cartas a cada jugador"
     * Cards are dealt one at a time in counter-clockwise order
     *
     * @param {number} dealerIndex - The index of the dealer (0-3)
     * @param {boolean} withTorito - Whether to deal with "torito" (one card face-up in first round)
     * @returns {Object} Object containing playerCards and toritoInfo
     */
    dealBacigaHand(dealerIndex, withTorito = false) {
        debugLog('🃏 [DealManager] dealBacigaHand called, dealerIndex:', dealerIndex, 'withTorito:', withTorito);

        // Reset player cards for new deal
        this.playerCards = {
            0: [],
            1: [],
            2: [],
            3: []
        };
        this.centerCards = []; // No center cards in Baciga

        const dealingOrder = this.getDealingOrder(dealerIndex);
        debugLog('🃏 [DealManager] Dealing order:', dealingOrder);

        // Determine which card will be face-up (torito) - always the first card dealt to each player
        const toritoInfo = withTorito ? {
            enabled: true,
            cardIndex: 0 // The first card (round 0) will be face-up
        } : {
            enabled: false,
            cardIndex: null
        };

        // Deal 3 cards to each player, one at a time in order
        // Round 1: each player gets 1 card
        // Round 2: each player gets 2nd card
        // Round 3: each player gets 3rd card
        for (let round = 0; round < 3; round++) {
            for (const playerIndex of dealingOrder) {
                const card = this.deck.dealOne();
                if (card) {
                    this.playerCards[playerIndex].push(card);
                    debugLog(`🃏 [DealManager] Dealt card to player ${playerIndex}:`, card.suit, card.value);
                }
            }
        }

        // Log final hands
        for (let i = 0; i < 4; i++) {
            debugLog(`🃏 [DealManager] Player ${i} final hand:`,
                this.playerCards[i].map(c => `${c.value} ${c.suit}`).join(', '));
        }

        return {
            centerCards: [],
            playerCards: this.playerCards,
            toritoInfo
        };
    }

    /**
     * Reset for a new round
     */
    reset() {
        this.isFirstDeal = true;
        this.centerCards = [];
        this.discardedCards = [];
        this.playerCards = {
            0: [],
            1: [],
            2: [],
            3: []
        };
    }

    /**
     * Set a new deck
     * @param {Deck} deck 
     */
    setDeck(deck) {
        this.deck = deck;
        this.reset();
    }

    /**
     * Get current center cards
     */
    getCenterCards() {
        return [...this.centerCards];
    }

    /**
     * Get cards for a specific player
     */
    getPlayerCards(playerIndex) {
        return [...(this.playerCards[playerIndex] || [])];
    }

    /**
     * Remove a card from center (when captured by a player)
     */
    removeCenterCard(card) {
        const index = this.centerCards.findIndex(c =>
            c.suit === card.suit && c.value === card.value
        );
        if (index !== -1) {
            this.centerCards.splice(index, 1);
        }
    }

    /**
     * Add cards to center (e.g., when no capture is made)
     */
    addCenterCards(cards) {
        this.centerCards.push(...cards);
    }

    /**
     * Add collected cards to a team's pile
     * @param {number} playerIndex - The player who collected (0-3)
     * @param {Card[]} cards - Array of cards collected
     */
    addCollectedCards(playerIndex, cards) {
        const team = playerIndex % 2 === 0 ? 'team1' : 'team2';
        this.collectedCards[team].push(...cards);
    }

    /**
     * Get number of collected cards for a team
     * @param {string} team - 'team1' or 'team2'
     * @returns {number}
     */
    getCollectedCount(team) {
        return this.collectedCards[team].length;
    }

    /**
     * Reset collected cards (for new round)
     */
    resetCollectedCards() {
        this.collectedCards = {
            team1: [],
            team2: []
        };
    }

    /**
     * Remove a card from a player's hand
     * @param {number} playerIndex
     * @param {Object} card
     * @returns {boolean} True if card was found and removed
     */
    removePlayerCard(playerIndex, card) {
        const cards = this.playerCards[playerIndex];
        const index = cards.findIndex(c => c.suit === card.suit && c.value === card.value);

        if (index !== -1) {
            cards.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Deal a single card to a player (for Baciga 31 phase)
     * @param {number} playerIndex
     * @returns {Object|null} The dealt card or null if deck is empty
     */
    dealCardToPlayer(playerIndex) {
        const card = this.deck.dealOne();
        if (card) {
            this.playerCards[playerIndex].push(card);
        }
        return card;
    }

    /**
     * Discard specific cards from a player's hand (for Baciga "paso")
     * @param {number} playerIndex - The player discarding
     * @param {number[]} cardIndices - Indices of cards to discard (sorted descending recommended)
     * @returns {Object[]} The discarded cards
     */
    discardPlayerCards(playerIndex, cardIndices) {
        debugLog(`🃏 [DealManager] Player ${playerIndex} discarding cards at indices:`, cardIndices);
        const discarded = [];
        // Sort indices descending to avoid index shifting
        const sortedIndices = [...cardIndices].sort((a, b) => b - a);

        for (const idx of sortedIndices) {
            if (this.playerCards[playerIndex] && this.playerCards[playerIndex][idx]) {
                const card = this.playerCards[playerIndex].splice(idx, 1)[0];

                // Clear assignedValue from wildcards when discarded
                // This ensures that if the card is dealt again, it doesn't retain its previous value
                if (card.assignedValue !== undefined) {
                    debugLog(`🃏 [DealManager] Clearing assignedValue ${card.assignedValue} from discarded wildcard:`, card.value, card.suit);
                    delete card.assignedValue;
                }

                discarded.push(card);
                // Store discarded cards for later
                this.discardedCards.push(card);
                debugLog(`🃏 [DealManager] Discarded:`, card.value, card.suit);
            }
        }

        debugLog(`🃏 [DealManager] Player ${playerIndex} now has ${this.playerCards[playerIndex].length} cards`);
        return discarded;
    }

    /**
     * Move all discarded cards to the bottom of the deck
     * This should be called before the Las 31 phase
     */
    moveDiscardedCardsToBottomOfDeck() {
        if (this.discardedCards.length > 0) {
            debugLog(`🃏 [DealManager] Moving ${this.discardedCards.length} discarded cards to bottom of deck`);
            debugLog(`🃏 [DealManager] Discarded cards being moved:`, this.discardedCards.map(c => `${c.value} ${c.suit}`).join(', '));
            debugLog(`🃏 [DealManager] Deck size before adding discarded cards: ${this.deck.remaining}`);
            this.deck.addToBottom([...this.discardedCards]);
            debugLog(`🃏 [DealManager] Deck size after adding discarded cards: ${this.deck.remaining}`);
            this.discardedCards = [];
        } else {
            debugLog(`🃏 [DealManager] No discarded cards to move`);
        }
    }

    /**
     * Get the current number of cards remaining in the deck
     * @returns {number} Number of cards in deck
     */
    getDeckSize() {
        return this.deck.remaining;
    }

    /**
     * Deal replacement cards to players after discarding (for Baciga "paso" round)
     * Each player gets cards to bring their hand back to 3
     * For player 0 (human), cards are inserted at the original discard positions
     * @param {number} dealerIndex - The dealer's index
     * @param {Object} pendingDiscards - { playerIndex: [cardIndices] }
     * @returns {Object} { playerIndex: { cards: [newCards], positions: [originalIndices] } }
     */
    dealReplacementCards(dealerIndex, pendingDiscards) {
        debugLog(`🃏 [DealManager] dealReplacementCards called, dealer:`, dealerIndex);
        debugLog(`🃏 [DealManager] Pending discards:`, pendingDiscards);

        const dealingOrder = this.getDealingOrder(dealerIndex);
        const newCards = {};

        // Generate replacement plan for all players
        for (const [playerIdxStr, cardIndices] of Object.entries(pendingDiscards)) {
            const playerIdx = parseInt(playerIdxStr);
            if (!newCards[playerIdx]) {
                newCards[playerIdx] = {
                    cards: [],
                    positions: [],
                    originalDiscardIndices: cardIndices ? [...cardIndices].sort((a, b) => a - b) : []
                };
            }
        }

        // First, execute all discards
        for (const [playerIdxStr, cardIndices] of Object.entries(pendingDiscards)) {
            const playerIdx = parseInt(playerIdxStr);
            if (cardIndices && cardIndices.length > 0) {
                this.discardPlayerCards(playerIdx, cardIndices);
            }
        }

        // Then deal replacement cards in order
        // Complete all cards for one player before moving to the next (not round-robin)
        const playerReplacementIndex = { 0: 0, 1: 0, 2: 0, 3: 0 };

        for (const playerIndex of dealingOrder) {
            while (this.playerCards[playerIndex].length < 3) {
                // If deck is empty mid-deal, recycle accumulated discarded cards before giving up
                if (this.deck.remaining === 0 && this.discardedCards.length > 0) {
                    debugLog(`🃏 [DealManager] Mazo vacío durante reparto de sustitución, reciclando ${this.discardedCards.length} cartas descartadas`);
                    this.deck.addToBottom([...this.discardedCards]);
                    this.discardedCards = [];
                }

                if (this.deck.remaining === 0) break; // Sin cartas disponibles, salir del bucle

                const card = this.deck.dealOne();
                if (!card) break;

                // Determine insertion position
                // If we have tracked discard indices, use them to fill gaps
                let insertPosition;
                const trackedDiscards = newCards[playerIndex]?.originalDiscardIndices;

                if (trackedDiscards && trackedDiscards.length > 0) {
                    // Use the next available discard slot
                    const replacementIdx = playerReplacementIndex[playerIndex];
                    if (replacementIdx < trackedDiscards.length) {
                        insertPosition = trackedDiscards[replacementIdx];
                    } else {
                        // Fallback (shouldn't happen if logic is correct): append
                        insertPosition = this.playerCards[playerIndex].length;
                    }
                } else {
                    // No tracked discards? Append (fallback)
                    insertPosition = this.playerCards[playerIndex].length;
                }

                // Insert card at the correct position
                this.playerCards[playerIndex].splice(insertPosition, 0, card);

                // Record for renderer
                if (!newCards[playerIndex]) newCards[playerIndex] = { cards: [], positions: [] };
                newCards[playerIndex].cards.push(card);
                newCards[playerIndex].positions.push(insertPosition);

                playerReplacementIndex[playerIndex]++;

                debugLog(`🃏 [DealManager] Dealt replacement to player ${playerIndex} at position ${insertPosition}:`, card.value, card.suit);
            }
        }

        // Log final hands
        for (let i = 0; i < 4; i++) {
            debugLog(`🃏 [DealManager] Player ${i} final hand after replacement:`,
                this.playerCards[i].map(c => `${c.value} ${c.suit}`).join(', '));
        }

        return newCards;
    }
}

export default DealManager;
